import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Check from '@mui/icons-material/Check';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import { Typography, Select } from '@proposify/ui';
import { fontSize } from '@proposify/tokens/semantic-tokens/fontSize';
import type { BillingFrequency } from '@proposify/pricing-engine';
import { BILLING_FREQUENCY_ORDER } from '@proposify/pricing-engine';
import { COLORS } from 'muiTheme/colors';
import { PanelSectionStyled, SectionHeaderStyled } from './DiscountSection';

// Width reserved for the selected-check so labels align across all items.
const CHECK_SLOT_WIDTH = 28;

interface BillingFrequencySectionProps {
  value: BillingFrequency;
  /**
   * SP-3792 seam: frequencies offered for new selections; undefined = all.
   * A row's current value stays visible even when not in the enabled set.
   */
  enabledFrequencies?: BillingFrequency[];
  onChange: (value: BillingFrequency) => void;
}

export const BillingFrequencySection = ({ value, enabledFrequencies, onChange }: BillingFrequencySectionProps) => {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      BILLING_FREQUENCY_ORDER.filter((frequency) => frequency === value || !enabledFrequencies || enabledFrequencies.includes(frequency)),
    [enabledFrequencies, value]
  );

  return (
    <PanelSectionStyled>
      <SectionHeaderStyled>
        <Typography fontWeight={600} fontSize={fontSize['3']}>
          {t('pricing.billing_frequency.label')}
        </Typography>
      </SectionHeaderStyled>
      <Select
        data-testid="billing-frequency-select"
        size="small"
        value={value}
        sx={{ '& .MuiSelect-select': { paddingLeft: '6px' } }}
        renderValue={(selected) => t(`pricing.billing_frequency.${String(selected)}`)}
        onChange={(event) => onChange(event.target.value as BillingFrequency)}
      >
        {options.map((frequency) => (
          <MenuItem
            key={frequency}
            value={frequency}
            data-testid={`billing-frequency-option-${frequency}`}
            sx={{ padding: '6px 16px', fontSize: fontSize['3'] }}
          >
            <ListItemIcon sx={{ '&&': { minWidth: CHECK_SLOT_WIDTH } }}>
              {frequency === value && (
                <Check fontSize="small" sx={{ color: COLORS.colorHeaderRow }} data-testid="billing-frequency-selected-icon" />
              )}
            </ListItemIcon>
            {t(`pricing.billing_frequency.${frequency}`)}
          </MenuItem>
        ))}
      </Select>
    </PanelSectionStyled>
  );
};
